import React, { useState, useEffect } from 'react';
import { X, Save, Building2 } from 'lucide-react';
import { Site, UpdateSiteRequest } from '../../services/api';
import { validate, collectErrors } from '../../utils/validation';

interface EditSiteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (siteId: string, site: UpdateSiteRequest) => Promise<void>;
    isUpdating: boolean;
    site: Site | null;
}

const EditSiteModal: React.FC<EditSiteModalProps> = ({ isOpen, onClose, onSubmit, isUpdating, site }) => {
    const [formData, setFormData] = useState<UpdateSiteRequest>({
        name: '',
        code: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        postcode: '',
        active: true,
    });
    const [errors, setErrors] = useState<Partial<Record<keyof UpdateSiteRequest, string>>>({});

    useEffect(() => {
        if (site) {
            setFormData({
                name: site.name || '',
                code: site.code || '',
                addressLine1: site.addressLine1 || '',
                addressLine2: site.addressLine2 || '',
                city: site.city || '',
                postcode: site.postcode || '',
                active: site.active,
            });
            setErrors({});
        }
    }, [site]);

    const validateForm = (): boolean => {
        const result = collectErrors({
            name: validate.required(formData.name, 'Site name'),
            code: validate.required(formData.code, 'Site code') ?? validate.siteCode(formData.code),
            addressLine1: validate.required(formData.addressLine1, 'Address line 1'),
            city: validate.required(formData.city, 'City'),
            postcode: validate.required(formData.postcode, 'Postcode') ?? validate.ukPostcode(formData.postcode ?? ''),
        });
        setErrors(result);
        return Object.keys(result).length === 0;
    };

    const handleChange = (field: keyof UpdateSiteRequest, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (typeof value === 'string' && errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!site) return;
        if (!validateForm()) return;
        try {
            await onSubmit(site.id, formData);
            onClose();
        } catch (error) {
            console.error('Error updating site:', error);
        }
    };

    if (!isOpen || !site) return null;

    const inputClass = (field: keyof UpdateSiteRequest, extra = '') =>
        `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${extra} ${errors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;

    const ringStyle = { '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.1)' }}>
                            <Building2 className="w-6 h-6" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Edit Site</h2>
                            <p className="text-sm text-gray-500">Update {site.name}'s information</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isUpdating}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">

                    {/* Site Details */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200 mb-3">
                            Site Details
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    disabled={isUpdating}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    className={inputClass('name')}
                                    style={ringStyle}
                                />
                                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Site Code *</label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    disabled={isUpdating}
                                    onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                                    className={inputClass('code', 'font-mono')}
                                    style={ringStyle}
                                />
                                {errors.code
                                    ? <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                                    : <p className="mt-1 text-xs text-gray-500">Unique identifier (letters, numbers, hyphens)</p>}
                            </div>
                        </div>
                    </div>

                    {/* Address */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200 mb-3">
                            Address
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 *</label>
                                <input
                                    type="text"
                                    placeholder="123 High Street"
                                    value={formData.addressLine1}
                                    disabled={isUpdating}
                                    onChange={(e) => handleChange('addressLine1', e.target.value)}
                                    className={inputClass('addressLine1')}
                                    style={ringStyle}
                                />
                                {errors.addressLine1 && <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                                <input
                                    type="text"
                                    placeholder="Building, suite, etc. (optional)"
                                    value={formData.addressLine2}
                                    disabled={isUpdating}
                                    onChange={(e) => handleChange('addressLine2', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                                    style={ringStyle}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                                    <input
                                        type="text"
                                        placeholder="London"
                                        value={formData.city}
                                        disabled={isUpdating}
                                        onChange={(e) => handleChange('city', e.target.value)}
                                        className={inputClass('city')}
                                        style={ringStyle}
                                    />
                                    {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode *</label>
                                    <input
                                        type="text"
                                        placeholder="SW1A 1AA"
                                        value={formData.postcode}
                                        disabled={isUpdating}
                                        onChange={(e) => handleChange('postcode', e.target.value.toUpperCase())}
                                        className={inputClass('postcode')}
                                        style={ringStyle}
                                    />
                                    {errors.postcode && <p className="mt-1 text-sm text-red-600">{errors.postcode}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200 mb-3">
                            Status
                        </h3>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="active"
                                checked={formData.active}
                                onChange={(e) => handleChange('active', e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 accent-[#69BE28]"
                                disabled={isUpdating}
                            />
                            <label htmlFor="active" className="ml-2 block text-sm text-gray-900">Active site</label>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Inactive sites cannot be assigned to new drivers</p>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isUpdating}
                            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            {isUpdating ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span>Updating...</span>
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    <span>Update Site</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditSiteModal;
